/**
* @license Apache-2.0
*
* Copyright (c) 2024 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import { isPrimitive as isString } from '@stdlib/assert/is-string';
import parse from './../../../base/parse';
import Complex128 from './../../../float64/ctor';
import format from '@stdlib/string/format';


// MAIN //

/**
* Parse a string representation of a complex number and returns a Complex128 instance.
*
* @param {string} str - string representation of a complex number
* @throws {TypeError} must provide a string
* @throws {Error} must provide a valid string representation of a complex number
* @returns {Complex128} 128-bit complex number
*
* @example
* var str = '1 + 2i';
* var z = parseComplex128( str );
* // returns <Complex128>
*/
function parseComplex128( str ) {
	var v;

	if ( !isString( str ) ) {
		throw new TypeError( format( 'invalid argument. First argument must be a string. Value: `%s`.', str ) );
	}
	v = parse( str );
	if ( v === null ) {
		throw new Error( format( 'invalid argument. Unable to parse input string as a complex number. Value: `%s`.', str ) );
	}
	return new Complex128( v.re, v.im );
}


// EXPORTS //

export default parseComplex128;
